import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Modal } from '../components/ui/Modal';
import { Pagination } from '../components/ui/Pagination';
import { useSearch } from '../hooks/useSearch';
import { useFilters } from '../hooks/useFilters';
import { usePagination } from '../hooks/usePagination';
import { ColumnCustomizer } from '../components/ColumnCustomizer';
import { Plus, Filter, Download, Eye, Edit, Search, FileText, Euro, Calendar, LayoutGrid, List as ListIcon, AlertTriangle, CheckCircle2, X, Save } from 'lucide-react';
import { useRBAC } from '../contexts/RBACContext';
import { PermissionGate } from '../components/PermissionGate';
import { ProgressiveNumberInput } from '../components/ProgressiveNumberInput';
import { mockData } from '../services/mockData';
import { motion, AnimatePresence } from 'framer-motion';
const initialContractsData = mockData.getContracts();
const columnsConfig = [{
  key: 'number',
  label: 'N° Contratto'
}, {
  key: 'client',
  label: 'Cliente'
}, {
  key: 'product',
  label: 'Prodotto'
}, {
  key: 'status',
  label: 'Stato'
}, {
  key: 'amount',
  label: 'Importo'
}, {
  key: 'expiry',
  label: 'Scadenza'
}, {
  key: 'actions',
  label: 'Azioni'
}];
// Updated Kanban columns as per specs
const kanbanColumns = [{
  id: 'da_proporre',
  title: 'Da proporre',
  color: 'bg-slate-50 border-slate-200'
}, {
  id: 'proposto',
  title: 'Proposto',
  color: 'bg-blue-50 border-blue-200'
}, {
  id: 'in_contrattazione',
  title: 'In contrattazione',
  color: 'bg-yellow-50 border-yellow-200'
}, {
  id: 'accettato',
  title: 'Accettato',
  color: 'bg-green-50 border-green-200'
}, {
  id: 'negativo',
  title: 'Negativo',
  color: 'bg-red-50 border-red-200'
}, {
  id: 'sospeso',
  title: 'Sospeso',
  color: 'bg-orange-50 border-orange-200'
}, {
  id: 'rinnovato',
  title: 'Rinnovato',
  color: 'bg-emerald-50 border-emerald-200'
}, {
  id: 'concluso',
  title: 'Concluso',
  color: 'bg-slate-100 border-slate-300'
}];
const statusLabels: Record<string, string> = {
  da_proporre: 'Da proporre',
  proposto: 'Proposto',
  in_contrattazione: 'In contrattazione',
  accettato: 'Accettato',
  negativo: 'Negativo',
  sospeso: 'Sospeso',
  rinnovato: 'Rinnovato',
  concluso: 'Concluso',
  // Legacy mappings
  proposal: 'Da proporre',
  pending: 'In contrattazione',
  urgent: 'Da sollecitare',
  accepted: 'Accettato',
  rejected: 'Negativo',
  revoked: 'Revocato'
};
export function ContractsPage() {
  const {
    canCreate,
    canEdit
  } = useRBAC();
  const [viewMode, setViewMode] = useState<'list' | 'kanban'>('kanban');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(columnsConfig.map(c => c.key));
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [kanbanData, setKanbanData] = useState(initialContractsData);
  const [draggedCard, setDraggedCard] = useState<any>(null);
  const {
    filters,
    setFilter,
    clearFilters,
    filteredData: filterResult
  } = useFilters(kanbanData);
  const {
    searchTerm,
    setSearchTerm,
    filteredData: searchResult
  } = useSearch(filterResult, ['number', 'client']);
  const {
    currentPage,
    totalPages,
    paginatedData,
    goToPage,
    pageSize,
    setPageSize,
    totalItems,
    setCurrentPage
  } = usePagination(searchResult);
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchTerm]);
  const handleView = (item: any) => {
    setSelectedItem(item);
    setIsViewModalOpen(true);
  };
  const handleEdit = (item: any) => {
    setSelectedItem(item);
    setIsModalOpen(true);
  };
  const handleCreate = () => {
    setSelectedItem(null);
    setIsModalOpen(true);
  };
  const handleSave = () => {
    setSaveSuccess(true);
    setTimeout(() => {
      setSaveSuccess(false);
      setIsModalOpen(false);
    }, 1500);
  };
  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, card: any) => {
    setDraggedCard(card);
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  const handleDrop = (e: React.DragEvent, targetColumnId: string) => {
    e.preventDefault();
    if (draggedCard && draggedCard.status !== targetColumnId) {
      setKanbanData(prev => prev.map(item => item.id === draggedCard.id ? {
        ...item,
        status: targetColumnId
      } : item));
    }
    setDraggedCard(null);
  };
  const getCardsByColumn = (columnId: string) => {
    // Map legacy statuses to new ones
    const statusMap: Record<string, string[]> = {
      da_proporre: ['da_proporre', 'proposal'],
      proposto: ['proposto'],
      in_contrattazione: ['in_contrattazione', 'pending'],
      accettato: ['accettato', 'accepted'],
      negativo: ['negativo', 'rejected'],
      sospeso: ['sospeso', 'urgent'],
      rinnovato: ['rinnovato'],
      concluso: ['concluso', 'revoked']
    };
    const validStatuses = statusMap[columnId] || [columnId];
    return searchResult.filter(c => validStatuses.includes(c.status));
  };
  return <Layout data-id="element-1211">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4" data-id="element-1212">
        <div data-id="element-1213">
          <h1 className="text-3xl font-bold text-seform-navy" data-id="element-1214">
            Preventivi e contratti
          </h1>
          <p className="text-slate-500 mt-1" data-id="element-1215">
            Gestione del ciclo di vita dei contratti e delle offerte.
          </p>
        </div>
        <div className="flex gap-3" data-id="element-1216">
          <div className="bg-slate-100 p-1 rounded-xl flex items-center" data-id="element-1217">
            <button onClick={() => setViewMode('kanban')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${viewMode === 'kanban' ? 'bg-white text-seform-navy shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-1218">
              <LayoutGrid className="h-4 w-4 inline-block mr-1.5" data-id="element-1219" /> Kanban
            </button>
            <button onClick={() => setViewMode('list')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${viewMode === 'list' ? 'bg-white text-seform-navy shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-1220">
              <ListIcon className="h-4 w-4 inline-block mr-1.5" data-id="element-1221" /> Lista
            </button>
          </div>
          <Button variant="flat" color="secondary" startContent={<Download className="h-4 w-4" data-id="element-1223" />} data-id="element-1222">
            Esporta
          </Button>
          <PermissionGate module="contracts" action="create" data-id="element-1224">
            <Button onClick={handleCreate} startContent={<Plus className="h-4 w-4" data-id="element-1226" />} data-id="element-1225">
              Nuovo preventivo
            </Button>
          </PermissionGate>
        </div>
      </div>

      <Card className="mb-6" noPadding data-id="element-1227">
        <div className="p-4 flex flex-col gap-4" data-id="element-1228">
          <div className="flex flex-col sm:flex-row gap-4" data-id="element-1229">
            <div className="flex-1" data-id="element-1230">
              <Input placeholder="Cerca contratto, cliente..." startContent={<Search className="h-4 w-4" data-id="element-1232" />} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} fullWidth data-id="element-1231" />
            </div>
            <div className="flex gap-2" data-id="element-1233">
              <Button variant={showFilters ? 'solid' : 'flat'} color="secondary" onClick={() => setShowFilters(!showFilters)} startContent={<Filter className="h-4 w-4" data-id="element-1235" />} data-id="element-1234">
                Filtri{' '}
                {Object.keys(filters).filter(k => filters[k]).length > 0 && <Badge variant="primary" size="sm" className="ml-1" data-id="element-1236">
                    {Object.keys(filters).filter(k => filters[k]).length}
                  </Badge>}
              </Button>
              {viewMode === 'list' && <ColumnCustomizer columns={columnsConfig} visibleColumns={visibleColumns} onColumnChange={setVisibleColumns} pageKey="contracts" data-id="element-1237" />}
            </div>
          </div>

          {showFilters && <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-slate-100 animate-in fade-in slide-in-from-top-2" data-id="element-1238">
              <Select label="Stato" value={filters.status || ''} onChange={e => setFilter('status', e.target.value)} options={[{
            value: '',
            label: 'Tutti'
          }, {
            value: 'da_proporre',
            label: 'Da proporre'
          }, {
            value: 'proposto',
            label: 'Proposto'
          }, {
            value: 'in_contrattazione',
            label: 'In contrattazione'
          }, {
            value: 'accettato',
            label: 'Accettato'
          }, {
            value: 'negativo',
            label: 'Negativo'
          }, {
            value: 'sospeso',
            label: 'Sospeso'
          }, {
            value: 'rinnovato',
            label: 'Rinnovato'
          }, {
            value: 'concluso',
            label: 'Concluso'
          }, {
            value: 'da_sollecitare',
            label: 'Da sollecitare'
          }, {
            value: 'non_accettato',
            label: 'Non accettato'
          }, {
            value: 'revocato',
            label: 'Revocato'
          }]} data-id="element-1239" />
              <div className="col-span-full flex justify-end" data-id="element-1240">
                <Button variant="flat" color="danger" size="sm" onClick={clearFilters} startContent={<X className="h-4 w-4" data-id="element-1242" />} data-id="element-1241">
                  Resetta filtri
                </Button>
              </div>
            </div>}
        </div>
      </Card>

      {viewMode === 'list' ? <Card noPadding className="overflow-hidden" data-id="element-1243">
          <div className="overflow-x-auto" data-id="element-1244">
            <table className="w-full text-left border-collapse table-fixed" data-id="element-1245">
              <thead data-id="element-1246">
                <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-1247">
                  {columnsConfig.filter(c => visibleColumns.includes(c.key)).map(col => <th key={col.key} className={`px-6 py-4 ${col.key === 'actions' ? 'text-right w-28' : ''}`} data-id="element-1248">
                        {col.label}
                      </th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-1249">
                {paginatedData.map(item => <tr key={item.id} className="hover:bg-slate-50/80 transition-colors group" data-id="element-1250">
                    {visibleColumns.includes('number') && <td className="px-6 py-4 font-mono text-xs text-slate-500" data-id="element-1251">
                        {item.number}
                      </td>}
                    {visibleColumns.includes('client') && <td className="px-6 py-4 font-medium text-slate-900" data-id="element-1252">
                        {item.client}
                      </td>}
                    {visibleColumns.includes('product') && <td className="px-6 py-4 text-sm text-slate-600" data-id="element-1253">
                        {item.product}
                      </td>}
                    {visibleColumns.includes('status') && <td className="px-6 py-4" data-id="element-1254">
                        <Badge variant={['accettato', 'accepted', 'rinnovato'].includes(item.status) ? 'success' : ['in_contrattazione', 'pending', 'proposto'].includes(item.status) ? 'warning' : ['negativo', 'rejected', 'revocato', 'revoked'].includes(item.status) ? 'danger' : 'neutral'} data-id="element-1255">
                          {statusLabels[item.status] || item.status}
                        </Badge>
                      </td>}
                    {visibleColumns.includes('amount') && <td className="px-6 py-4 text-sm font-mono text-slate-900" data-id="element-1256">
                        {item.amount}
                      </td>}
                    {visibleColumns.includes('expiry') && <td className="px-6 py-4 text-sm text-slate-600" data-id="element-1257">
                        {item.expiry}
                      </td>}
                    {visibleColumns.includes('actions') && <td className="px-6 py-4 text-right" data-id="element-1258">
                        <div className="flex items-center justify-end gap-2" data-id="element-1259">
                          <Button variant="light" size="sm" className="!px-2" onClick={() => handleView(item)} data-id="element-1260">
                            <Eye className="h-4 w-4 text-slate-500" data-id="element-1261" />
                          </Button>
                          <PermissionGate module="contracts" action="edit" data-id="element-1262">
                            <Button variant="light" size="sm" className="!px-2" onClick={() => handleEdit(item)} data-id="element-1263">
                              <Edit className="h-4 w-4 text-seform-orange" data-id="element-1264" />
                            </Button>
                          </PermissionGate>
                        </div>
                      </td>}
                  </tr>)}
              </tbody>
            </table>
          </div>
          <div className="border-t border-slate-100" data-id="element-1265">
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-1266" />
          </div>
        </Card> :
    // Kanban View with fixed scroll
    <div className="overflow-x-auto pb-4" data-id="element-1267">
          <div className="flex gap-4 min-w-max" data-id="element-1268">
            {kanbanColumns.map(column => {
          const cards = getCardsByColumn(column.id);
          return <div key={column.id} className={`w-72 flex-shrink-0 rounded-xl border ${column.color} flex flex-col max-h-[calc(100vh-280px)]`} onDragOver={handleDragOver} onDrop={e => handleDrop(e, column.id)} data-id="element-1269">
                  <div className="p-3 border-b border-slate-200/50 flex items-center justify-between" data-id="element-1270">
                    <h3 className="font-semibold text-slate-700 text-sm" data-id="element-1271">
                      {column.title}
                    </h3>
                    <Badge variant="neutral" size="sm" data-id="element-1272">
                      {cards.length}
                    </Badge>
                  </div>
                  <div className="flex-1 overflow-y-auto p-2 space-y-2" data-id="element-1273">
                    {cards.map(card => <div key={card.id} draggable onDragStart={e => handleDragStart(e, card)} onClick={() => handleView(card)} className={`bg-white rounded-lg p-3 shadow-sm border border-slate-100 cursor-pointer hover:shadow-md transition-all ${draggedCard?.id === card.id ? 'opacity-50' : ''}`} data-id="element-1274">
                        <div className="flex items-start justify-between mb-2" data-id="element-1275">
                          <span className="text-xs font-mono text-slate-400" data-id="element-1276">
                            {card.number}
                          </span>
                          <div className="w-6 h-6 rounded-full bg-seform-navy/10 flex items-center justify-center text-[10px] font-bold text-seform-navy" data-id="element-1277">
                            AL
                          </div>
                        </div>
                        <h4 className="font-semibold text-slate-900 text-sm mb-1 line-clamp-1" data-id="element-1278">
                          {card.client}
                        </h4>
                        <p className="text-xs text-slate-500 mb-2 line-clamp-1" data-id="element-1279">
                          {card.product}
                        </p>
                        <div className="flex items-center justify-between" data-id="element-1280">
                          <span className="text-sm font-mono font-medium text-seform-navy" data-id="element-1281">
                            {card.amount}
                          </span>
                          <span className="text-xs text-slate-400" data-id="element-1282">
                            {card.expiry}
                          </span>
                        </div>
                      </div>)}
                    {cards.length === 0 && <div className="text-center py-8 text-slate-400 text-sm" data-id="element-1283">
                        Nessun elemento
                      </div>}
                  </div>
                </div>;
        })}
          </div>
        </div>}

      {/* View Modal */}
      <Modal isOpen={isViewModalOpen} onClose={() => setIsViewModalOpen(false)} title="Dettaglio contratto" size="lg" data-id="element-1284">
        {selectedItem && <div className="space-y-6" data-id="element-1285">
            <div className="grid grid-cols-2 gap-4" data-id="element-1286">
              <div data-id="element-1287">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1288">
                  Numero
                </span>
                <p className="font-mono font-medium" data-id="element-1289">{selectedItem.number}</p>
              </div>
              <div data-id="element-1290">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1291">
                  Stato
                </span>
                <div className="mt-1" data-id="element-1292">
                  <Badge variant={['accettato', 'accepted', 'rinnovato'].includes(selectedItem.status) ? 'success' : ['in_contrattazione', 'pending', 'proposto'].includes(selectedItem.status) ? 'warning' : ['negativo', 'rejected'].includes(selectedItem.status) ? 'danger' : 'neutral'} data-id="element-1293">
                    {statusLabels[selectedItem.status] || selectedItem.status}
                  </Badge>
                </div>
              </div>
              <div data-id="element-1294">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1295">
                  Cliente
                </span>
                <p className="font-medium" data-id="element-1296">{selectedItem.client}</p>
              </div>
              <div data-id="element-1297">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1298">
                  Prodotto
                </span>
                <p className="font-medium" data-id="element-1299">{selectedItem.product}</p>
              </div>
              <div data-id="element-1300">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1301">
                  Importo
                </span>
                <p className="font-mono font-bold text-seform-navy" data-id="element-1302">
                  {selectedItem.amount}
                </p>
              </div>
              <div data-id="element-1303">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1304">
                  Scadenza
                </span>
                <p className="font-medium" data-id="element-1305">{selectedItem.expiry}</p>
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-1306">
              <Button variant="flat" color="default" onClick={() => setIsViewModalOpen(false)} data-id="element-1307">
                Chiudi
              </Button>
              <PermissionGate module="contracts" action="edit" data-id="element-1308">
                <Button onClick={() => {
              setIsViewModalOpen(false);
              handleEdit(selectedItem);
            }} startContent={<Edit className="h-4 w-4" data-id="element-1310" />} data-id="element-1309">
                  Modifica
                </Button>
              </PermissionGate>
            </div>
          </div>}
      </Modal>

      {/* Edit Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={selectedItem ? 'Modifica contratto' : 'Nuovo preventivo'} size="2xl" data-id="element-1311">
        <AnimatePresence mode="wait" data-id="element-1312">
          {saveSuccess ? <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="flex flex-col items-center justify-center py-16" data-id="element-1313">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 15
          }} className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-1314">
                <CheckCircle2 className="w-12 h-12 text-green-500" data-id="element-1315" />
              </motion.div>
              <p className="text-lg font-semibold text-slate-800" data-id="element-1316">
                Contratto salvato con successo
              </p>
            </motion.div> : <motion.div key="form" initial={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-1317">
              <div className="space-y-8" data-id="element-1318">
                <section data-id="element-1319">
                  <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-1320">
                    <FileText className="h-4 w-4" data-id="element-1321" /> Dati generali
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1322">
                    <ProgressiveNumberInput type="contract" value={selectedItem?.number} onChange={() => {}} disabled={!!selectedItem} data-id="element-1323" />
                    <Select label="Cliente" defaultValue={selectedItem?.client} options={[{
                  value: 'Azienda 1',
                  label: 'Azienda 1'
                }, {
                  value: 'Azienda 2',
                  label: 'Azienda 2'
                }]} required data-id="element-1324" />
                    <Select label="Prodotto" defaultValue={selectedItem?.product} options={[{
                  value: 'sicurezza',
                  label: 'Consulenza sicurezza'
                }, {
                  value: 'haccp',
                  label: 'HACCP base'
                }, {
                  value: 'privacy',
                  label: 'Privacy GDPR'
                }]} required data-id="element-1325" />
                    <Select label="Responsabile sviluppo" options={[{
                  value: 'alessio',
                  label: 'Alessio'
                }, {
                  value: 'gianfranco',
                  label: 'Gian Franco'
                }]} required data-id="element-1326" />
                  </div>
                </section>

                <div className="h-px bg-slate-100" data-id="element-1327"></div>

                <section data-id="element-1328">
                  <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-1329">
                    <AlertTriangle className="h-4 w-4" data-id="element-1330" /> Stato e workflow
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1331">
                    <Select label="Stato attuale" defaultValue={selectedItem?.status || 'da_proporre'} options={[{
                  value: 'da_proporre',
                  label: 'Da proporre'
                }, {
                  value: 'proposto',
                  label: 'Proposto'
                }, {
                  value: 'in_contrattazione',
                  label: 'In contrattazione'
                }, {
                  value: 'accettato',
                  label: 'Accettato'
                }, {
                  value: 'negativo',
                  label: 'Negativo'
                }, {
                  value: 'sospeso',
                  label: 'Sospeso'
                }, {
                  value: 'rinnovato',
                  label: 'Rinnovato'
                }, {
                  value: 'concluso',
                  label: 'Concluso'
                }]} required data-id="element-1332" />
                    <Input label="Data cambio stato" type="date" defaultValue={new Date().toISOString().split('T')[0]} data-id="element-1333" />
                  </div>
                </section>

                <div className="h-px bg-slate-100" data-id="element-1334"></div>

                <section data-id="element-1335">
                  <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-1336">
                    <Euro className="h-4 w-4" data-id="element-1337" /> Dettagli economici
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1338">
                    <Input label="Importo (€)" type="number" placeholder="0.00" defaultValue={selectedItem?.amount?.replace('€ ', '')} required data-id="element-1339" />
                    <Input label="Durata (mesi)" type="number" defaultValue="12" required data-id="element-1340" />
                    <Input label="Data preventivo" type="date" defaultValue="2025-01-10" data-id="element-1341" />
                    <div className="flex items-center gap-2 pt-6" data-id="element-1342">
                      <input type="checkbox" id="renewal" className="rounded border-slate-300 text-seform-orange focus:ring-seform-orange" data-id="element-1343" />
                      <label htmlFor="renewal" className="text-sm text-slate-700" data-id="element-1344">
                        Tacito rinnovo
                      </label>
                    </div>
                  </div>
                </section>

                <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-1345">
                  <Button variant="flat" color="default" onClick={() => setIsModalOpen(false)} data-id="element-1346">
                    Annulla
                  </Button>
                  <Button onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-1348" />} data-id="element-1347">
                    Salva contratto
                  </Button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </Modal>
    </Layout>;
}